/**
 * UI Tools - Client-side tool definitions that the agent can use
 *
 * These tools allow the agent to control UI elements in the frontend.
 * The definitions are sent to the backend with each message request,
 * and the backend includes them in the agent's available tools.
 *
 * When the agent calls these tools, the backend sends a 'uiAction' event
 * back to the frontend, which the event handler processes.
 */

export interface UIToolParameter {
  type: 'string' | 'boolean' | 'number' | 'object' | 'array';
  description: string;
  enum?: string[];
  required?: boolean;
}

export interface UIToolDefinition {
  name: string;
  description: string;
  parameters: {
    type: 'object';
    properties: Record<string, UIToolParameter>;
    required?: string[];
  };
  // Maps to UIActionType and payload structure
  uiAction: string;
  payloadMapping?: Record<string, string>; // Maps tool param names to payload fields
}

/**
 * Available UI tools that the agent can use
 */
export const UI_TOOLS: UIToolDefinition[] = [
  {
    name: 'ui_open_browser',
    description: 'Opens a URL in the browser panel within the chat interface. Use this to show web pages, documentation, or references to the user. Note: Some sites block iframe embedding (Google, Facebook, Twitter). Sites that work well: Wikipedia, MDN, most documentation sites.',
    parameters: {
      type: 'object',
      properties: {
        url: {
          type: 'string',
          description: 'The URL to open in the browser panel',
        },
        title: {
          type: 'string',
          description: 'Optional title for the browser tab',
        },
      },
      required: ['url'],
    },
    uiAction: 'openBrowser',
    payloadMapping: {
      url: 'url',
      title: 'title',
    },
  },
  {
    name: 'ui_open_notes',
    description: 'Opens the notes panel showing the user\'s notes list. Use this when the user wants to view, create, or manage their notes.',
    parameters: {
      type: 'object',
      properties: {},
    },
    uiAction: 'openNotesList',
  },
  {
    name: 'ui_create_note',
    description: 'Creates a new note and opens it for editing in the notes panel.',
    parameters: {
      type: 'object',
      properties: {
        title: {
          type: 'string',
          description: 'Initial title for the note',
        },
        content: {
          type: 'string',
          description: 'Initial content for the note',
        },
      },
    },
    uiAction: 'createNote',
    payloadMapping: {
      title: 'title',
      content: 'content',
    },
  },
  {
    name: 'ui_close_panel',
    description: 'Closes the artifact panel (browser, notes, etc.) to give more space to the chat.',
    parameters: {
      type: 'object',
      properties: {},
    },
    uiAction: 'closeArtifactPanel',
  },
];

/**
 * Get tool definitions in the format expected by the backend/LLM
 */
export function getUIToolDefinitions() {
  return UI_TOOLS.map(tool => ({
    name: tool.name,
    description: tool.description,
    parameters: tool.parameters,
  }));
}

/**
 * Get the UI action mapping for a tool
 */
export function getUIActionForTool(toolName: string): { action: string; payloadMapping?: Record<string, string> } | null {
  const tool = UI_TOOLS.find(t => t.name === toolName);
  if (!tool) return null;

  return {
    action: tool.uiAction,
    payloadMapping: tool.payloadMapping,
  };
}
